import {
  EDITOR_COMPONENT,
  EDITOR_PREFIX,
  EditorComponent,
  type IElementPosition
} from './constants'
import './assets/css/date-picker.css'
import type { Draw } from '@vervedoc/docx-editor-view'

/** 日期选择器多语言文案接口 */
export interface IDatePickerLang {
  /** "现在"按钮文案 */
  now: string
  /** "确定"按钮文案 */
  confirm: string
  /** "返回"按钮文案 */
  return: string
  /** "选择时间"按钮文案 */
  timeSelect: string
  /** 星期文案 */
  weeks: {
    /** 周日文案 */
    sun: string
    /** 周一文案 */
    mon: string
    /** 周二文案 */
    tue: string
    /** 周三文案 */
    wed: string
    /** 周四文案 */
    thu: string
    /** 周五文案 */
    fri: string
    /** 周六文案 */
    sat: string
  }
  /** "年"文案 */
  year: string
  /** "月"文案 */
  month: string
  /** "时"文案 */
  hour: string
  /** "分"文案 */
  minute: string
  /** "秒"文案 */
  second: string
}

/** 日期选择器选项接口 */
export interface IDatePickerOption {
  /** 用户提交日期时的回调函数 */
  onSubmit?: (date: string) => any
}

/** 日期选择器内部 DOM 结构引用 */
interface IDatePickerDom {
  /** 最外层容器 */
  container: HTMLDivElement
  /** 日期面板包裹元素 */
  dateWrap: HTMLDivElement
  /** 星期标题行 */
  datePickerWeek: HTMLDivElement
  /** 时间面板包裹元素 */
  timeWrap: HTMLUListElement
  /** 标题栏（年月切换按钮） */
  title: {
    /** 上一年按钮 */
    preYear: HTMLSpanElement
    /** 上一月按钮 */
    preMonth: HTMLSpanElement
    /** 当前年月显示 */
    now: HTMLSpanElement
    /** 下一月按钮 */
    nextMonth: HTMLSpanElement
    /** 下一年按钮 */
    nextYear: HTMLSpanElement
  }
  /** 日期网格容器 */
  day: HTMLDivElement
  /** 时分秒选择列表 */
  time: {
    /** 小时列表 */
    hour: HTMLOListElement
    /** 分钟列表 */
    minute: HTMLOListElement
    /** 秒列表 */
    second: HTMLOListElement
  }
  /** 底部菜单按钮 */
  menu: {
    /** 切换时间选择按钮 */
    time: HTMLButtonElement
    /** "现在"按钮 */
    now: HTMLButtonElement
    /** "确定"按钮 */
    submit: HTMLButtonElement
  }
}

/** 渲染选项 */
interface IRenderOption {
  /** 当前日期值 */
  value: string
  /** 元素位置信息 */
  position: IElementPosition
  /** 日期格式化模板 */
  dateFormat?: string
}

/** 日期选择器组件，提供日期/时间选择能力并写回文档 */
export class DatePicker {
  /** 编辑器绘制实例 */
  private draw: Draw
  /** 外部传入的选项 */
  private options: IDatePickerOption
  /** 当前浏览的日期（用于面板显示年月） */
  private now: Date
  /** DOM 引用 */
  private dom: IDatePickerDom
  /** 当前渲染选项 */
  private renderOptions: IRenderOption | null
  /** 是否处于日期选择模式（true=日期，false=时间） */
  private isDatePicker: boolean
  /** 用户已选定的日期 */
  private pickDate: Date | null
  /** 多语言文案 */
  private lang: IDatePickerLang

  /**
   * 创建日期选择器实例
   * @param draw 编辑器绘制实例
   * @param options 选项
   */
  constructor(draw: Draw, options: IDatePickerOption = {}) {
    this.draw = draw
    this.options = options
    this.lang = this._getLang()
    this.now = new Date()
    this.dom = this._createDom()
    this.renderOptions = null
    this.isDatePicker = true
    this.pickDate = null
    this._bindEvent()
  }

  /**
   * 构建日期选择器 DOM 结构
   * @returns DOM 引用对象
   */
  private _createDom(): IDatePickerDom {
    const datePickerContainer = document.createElement('div')
    datePickerContainer.classList.add(`${EDITOR_PREFIX}-date-container`)
    datePickerContainer.setAttribute(EDITOR_COMPONENT, EditorComponent.POPUP)
    // title-切换年月、年月显示
    const dateWrap = document.createElement('div')
    dateWrap.classList.add(`${EDITOR_PREFIX}-date-wrap`)
    const datePickerTitle = document.createElement('div')
    datePickerTitle.classList.add(`${EDITOR_PREFIX}-date-title`)
    const preYearTitle = document.createElement('span')
    preYearTitle.classList.add(`${EDITOR_PREFIX}-date-title__pre-year`)
    preYearTitle.innerText = `<<`
    const preMonthTitle = document.createElement('span')
    preMonthTitle.classList.add(`${EDITOR_PREFIX}-date-title__pre-month`)
    preMonthTitle.innerText = `<`
    const nowTitle = document.createElement('span')
    nowTitle.classList.add(`${EDITOR_PREFIX}-date-title__now`)
    const nextMonthTitle = document.createElement('span')
    nextMonthTitle.classList.add(`${EDITOR_PREFIX}-date-title__next-month`)
    nextMonthTitle.innerText = `>`
    const nextYearTitle = document.createElement('span')
    nextYearTitle.classList.add(`${EDITOR_PREFIX}-date-title__next-year`)
    nextYearTitle.innerText = `>>`
    datePickerTitle.append(preYearTitle)
    datePickerTitle.append(preMonthTitle)
    datePickerTitle.append(nowTitle)
    datePickerTitle.append(nextMonthTitle)
    datePickerTitle.append(nextYearTitle)
    // week-星期显示
    const datePickerWeek = document.createElement('div')
    datePickerWeek.classList.add(`${EDITOR_PREFIX}-date-week`)
    const {
      weeks: { sun, mon, tue, wed, thu, fri, sat }
    } = this.lang
    const weekList = [sun, mon, tue, wed, thu, fri, sat]
    weekList.forEach(week => {
      const weekDom = document.createElement('span')
      weekDom.innerText = `${week}`
      datePickerWeek.append(weekDom)
    })
    // day-天数显示
    const datePickerDay = document.createElement('div')
    datePickerDay.classList.add(`${EDITOR_PREFIX}-date-day`)
    // 日期内容构建
    dateWrap.append(datePickerTitle)
    dateWrap.append(datePickerWeek)
    dateWrap.append(datePickerDay)
    // time-时间选择
    const timeWrap = document.createElement('ul')
    timeWrap.classList.add(`${EDITOR_PREFIX}-time-wrap`)
    let hourTime: HTMLOListElement
    let minuteTime: HTMLOListElement
    let secondTime: HTMLOListElement
    const timeList = [this.lang.hour, this.lang.minute, this.lang.second]
    timeList.forEach((t, i) => {
      const li = document.createElement('li')
      const timeText = document.createElement('span')
      timeText.innerText = t
      li.append(timeText)
      const ol = document.createElement('ol')
      const isHour = i === 0
      const isMinute = i === 1
      const endIndex = isHour ? 24 : 60
      for (let i = 0; i < endIndex; i++) {
        const time = document.createElement('li')
        time.innerText = `${String(i).padStart(2, '0')}`
        time.setAttribute('data-id', `${i}`)
        ol.append(time)
      }
      if (isHour) {
        hourTime = ol
      } else if (isMinute) {
        minuteTime = ol
      } else {
        secondTime = ol
      }
      li.append(ol)
      timeWrap.append(li)
    })
    // menu-选择时间、现在、确定
    const datePickerMenu = document.createElement('div')
    datePickerMenu.classList.add(`${EDITOR_PREFIX}-date-menu`)
    const timeMenu = document.createElement('button')
    timeMenu.classList.add(`${EDITOR_PREFIX}-date-menu__time`)
    timeMenu.innerText = this.lang.timeSelect
    const nowMenu = document.createElement('button')
    nowMenu.classList.add(`${EDITOR_PREFIX}-date-menu__now`)
    nowMenu.innerText = this.lang.now
    const submitMenu = document.createElement('button')
    submitMenu.classList.add(`${EDITOR_PREFIX}-date-menu__submit`)
    submitMenu.innerText = this.lang.confirm
    datePickerMenu.append(timeMenu)
    datePickerMenu.append(nowMenu)
    datePickerMenu.append(submitMenu)
    // 构建
    datePickerContainer.append(dateWrap)
    datePickerContainer.append(timeWrap)
    datePickerContainer.append(datePickerMenu)

    return {
      container: datePickerContainer,
      dateWrap,
      datePickerWeek,
      timeWrap,
      title: {
        preYear: preYearTitle,
        preMonth: preMonthTitle,
        now: nowTitle,
        nextMonth: nextMonthTitle,
        nextYear: nextYearTitle
      },
      day: datePickerDay,
      time: {
        hour: hourTime!,
        minute: minuteTime!,
        second: secondTime!
      },
      menu: {
        time: timeMenu,
        now: nowMenu,
        submit: submitMenu
      }
    }
  }

  /** 绑定 DOM 事件监听 */
  private _bindEvent() {
    this.dom.title.preYear.onclick = () => {
      this._preYear()
    }
    this.dom.title.preMonth.onclick = () => {
      this._preMonth()
    }
    this.dom.title.nextMonth.onclick = () => {
      this._nextMonth()
    }
    this.dom.title.nextYear.onclick = () => {
      this._nextYear()
    }
    this.dom.menu.time.onclick = () => {
      this.isDatePicker = !this.isDatePicker
      this._toggleDateTimePicker()
    }
    this.dom.menu.now.onclick = () => {
      this._now()
      this._submit()
    }
    this.dom.menu.submit.onclick = () => {
      this.dispose()
      this._submit()
    }
    this.dom.time.hour.onclick = evt => {
      if (!this.pickDate) return
      const li = <HTMLLIElement>evt.target
      const id = li.dataset.id
      if (!id) return
      this.pickDate.setHours(Number(id))
      this._setTimePick(false)
    }
    this.dom.time.minute.onclick = evt => {
      if (!this.pickDate) return
      const li = <HTMLLIElement>evt.target
      const id = li.dataset.id
      if (!id) return
      this.pickDate.setMinutes(Number(id))
      this._setTimePick(false)
    }
    this.dom.time.second.onclick = evt => {
      if (!this.pickDate) return
      const li = <HTMLLIElement>evt.target
      const id = li.dataset.id
      if (!id) return
      this.pickDate.setSeconds(Number(id))
      this._setTimePick(false)
    }
  }

  /** 根据元素位置计算并设置选择器在容器中的坐标 */
  private _setPosition() {
    if (!this.renderOptions) return
    const {
      position: {
        coordinate: {
          leftTop: [left, top]
        },
        lineHeight,
        pageNo
      }
    } = this.renderOptions
    const layout = this.draw.getLayout()
    const height = layout?.pages?.[0]?.rect?.height
      ?? this.draw.getOptions().pageHeight
      ?? 1123
    const pageGap = (this.draw.getOptions() as { pageGap?: number }).pageGap ?? 24
    const currentPageNo = pageNo ?? 0
    const preY = currentPageNo * (height + pageGap)
    // 位置
    this.dom.container.style.left = `${left}px`
    this.dom.container.style.top = `${top + preY + lineHeight}px`
  }

  /**
   * 判断日期是否无效
   * @param value 待校验日期
   * @returns 是否为无效日期
   */
  public isInvalidDate(value: Date): boolean {
    return value.toDateString() === 'Invalid Date'
  }

  /** 根据渲染选项初始化当前日期与已选日期 */
  private _setValue() {
    const value = this.renderOptions?.value
    if (value) {
      const setDate = new Date(value)
      this.now = this.isInvalidDate(setDate) ? new Date() : setDate
    } else {
      this.now = new Date()
    }
    this.pickDate = new Date(this.now)
  }

  /** 获取多语言文案，支持从 i18n 实例取值或回退到默认中文 */
  private _getLang() {
    const i18n = (this.draw as any).getI18n?.()
    const defaults: Record<string, string> = {
      'datePicker.now': '现在',
      'datePicker.confirm': '确定',
      'datePicker.return': '返回',
      'datePicker.timeSelect': '选择时间',
      'datePicker.weeks.sun': '日',
      'datePicker.weeks.mon': '一',
      'datePicker.weeks.tue': '二',
      'datePicker.weeks.wed': '三',
      'datePicker.weeks.thu': '四',
      'datePicker.weeks.fri': '五',
      'datePicker.weeks.sat': '六',
      'datePicker.year': '年',
      'datePicker.month': '月',
      'datePicker.hour': '时',
      'datePicker.minute': '分',
      'datePicker.second': '秒'
    }
    const t = i18n?.t ? i18n.t.bind(i18n) : (key: string) => defaults[key] ?? key
    return {
      now: t('datePicker.now'),
      confirm: t('datePicker.confirm'),
      return: t('datePicker.return'),
      timeSelect: t('datePicker.timeSelect'),
      weeks: {
        sun: t('datePicker.weeks.sun'),
        mon: t('datePicker.weeks.mon'),
        tue: t('datePicker.weeks.tue'),
        wed: t('datePicker.weeks.wed'),
        thu: t('datePicker.weeks.thu'),
        fri: t('datePicker.weeks.fri'),
        sat: t('datePicker.weeks.sat')
      },
      year: t('datePicker.year'),
      month: t('datePicker.month'),
      hour: t('datePicker.hour'),
      minute: t('datePicker.minute'),
      second: t('datePicker.second')
    }
  }

  /** 语言变更后刷新 DOM 文案 */
  private _setLangChange() {
    this.dom.menu.time.innerText = this.lang.timeSelect
    this.dom.menu.now.innerText = this.lang.now
    this.dom.menu.submit.innerText = this.lang.confirm
    const {
      weeks: { sun, mon, tue, wed, thu, fri, sat }
    } = this.lang
    const weekList = [sun, mon, tue, wed, thu, fri, sat]
    this.dom.datePickerWeek.childNodes.forEach((child, i) => {
      const childElement = <HTMLSpanElement>child
      childElement.innerText = weekList[i]
    })
    const hourTitle = <HTMLSpanElement>this.dom.time.hour.previousElementSibling
    hourTitle.innerText = this.lang.hour
    const minuteTitle = <HTMLSpanElement>(
      this.dom.time.minute.previousElementSibling
    )
    minuteTitle.innerText = this.lang.minute
    const secondTitle = <HTMLSpanElement>(
      this.dom.time.second.previousElementSibling
    )
    secondTitle.innerText = this.lang.second
  }

  /** 重新渲染日期网格（包含上下月填充与高亮状态） */
  private _update() {
    // 本地年月日
    const localDate = new Date()
    const localYear = localDate.getFullYear()
    const localMonth = localDate.getMonth() + 1
    const localDay = localDate.getDate()
    // 选择年月日
    let pickYear: number | null = null
    let pickMonth: number | null = null
    let pickDay: number | null = null
    if (this.pickDate) {
      pickYear = this.pickDate.getFullYear()
      pickMonth = this.pickDate.getMonth() + 1
      pickDay = this.pickDate.getDate()
    }
    // 当前年月日
    const year = this.now.getFullYear()
    const month = this.now.getMonth() + 1
    this.dom.title.now.innerText = `${year}${this.lang.year} ${String(
      month
    ).padStart(2, '0')}${this.lang.month}`
    // 日期补差
    const curDate = new Date(year, month, 0) // 当月日期
    const curDay = curDate.getDate() // 当月总天数
    let curWeek = new Date(year, month - 1, 1).getDay() // 当月第一天星期几
    if (curWeek === 0) {
      curWeek = 7
    }
    const preDay = new Date(year, month - 1, 0).getDate() // 上个月天数
    this.dom.day.replaceChildren()
    // 渲染上个月日期
    const preStartDay = preDay - curWeek + 1
    for (let i = preStartDay; i <= preDay; i++) {
      const dayDom = document.createElement('div')
      dayDom.classList.add('disable')
      dayDom.innerText = `${i}`
      dayDom.onclick = () => {
        const newMonth = month - 2
        this.now = new Date(year, newMonth, i)
        this._setDatePick(year, newMonth, i)
      }
      this.dom.day.append(dayDom)
    }
    // 渲染当月日期
    for (let i = 1; i <= curDay; i++) {
      const dayDom = document.createElement('div')
      if (localYear === year && localMonth === month && localDay === i) {
        dayDom.classList.add('active')
      }
      if (
        this.pickDate &&
        pickYear === year &&
        pickMonth === month &&
        pickDay === i
      ) {
        dayDom.classList.add('select')
      }
      dayDom.innerText = `${i}`
      dayDom.onclick = evt => {
        const newMonth = month - 1
        this.now = new Date(year, newMonth, i)
        this._setDatePick(year, newMonth, i)
        evt.stopPropagation()
      }
      this.dom.day.append(dayDom)
    }
    // 渲染下月日期
    const nextEndDay = 6 * 7 - curWeek - curDay
    for (let i = 1; i <= nextEndDay; i++) {
      const dayDom = document.createElement('div')
      dayDom.classList.add('disable')
      dayDom.innerText = `${i}`
      dayDom.onclick = () => {
        this.now = new Date(year, month, i)
        this._setDatePick(year, month, i)
      }
      this.dom.day.append(dayDom)
    }
  }

  /** 在日期面板与时间面板之间切换显示 */
  private _toggleDateTimePicker() {
    if (this.isDatePicker) {
      this.dom.dateWrap.classList.add('active')
      this.dom.timeWrap.classList.remove('active')
      this.dom.menu.time.innerText = this.lang.timeSelect
    } else {
      this.dom.dateWrap.classList.remove('active')
      this.dom.timeWrap.classList.add('active')
      this.dom.menu.time.innerText = this.lang.return
      // 设置时分秒选择
      this._setTimePick()
    }
  }

  /**
   * 设置用户选定的日期
   * @param year 年
   * @param month 月（0 基）
   * @param day 日
   */
  private _setDatePick(year: number, month: number, day: number) {
    this.now = new Date(year, month, day)
    this.pickDate?.setFullYear(year)
    this.pickDate?.setMonth(month)
    this.pickDate?.setDate(day)
    this._update()
  }

  /**
   * 设置时分秒选择高亮
   * @param isIntoView 是否同步滚动到选中项
   */
  private _setTimePick(isIntoView = true) {
    const hour = this.pickDate?.getHours() || 0
    const minute = this.pickDate?.getMinutes() || 0
    const second = this.pickDate?.getSeconds() || 0
    const {
      hour: hourDom,
      minute: minuteDom,
      second: secondDom
    } = this.dom.time
    const timeDomList = [hourDom, minuteDom, secondDom]
    // 清空
    timeDomList.forEach(timeDom => {
      timeDom
        .querySelectorAll('li')
        .forEach(li => li.classList.remove('active'))
    })
    const pickList: [HTMLOListElement, number][] = [
      [hourDom, hour],
      [minuteDom, minute],
      [secondDom, second]
    ]
    pickList.forEach(([dom, time]) => {
      const pickDom = dom.querySelector<HTMLLIElement>(`[data-id='${time}']`)!
      pickDom.classList.add('active')
      if (isIntoView) {
        this._scrollIntoView(dom, pickDom)
      }
    })
  }

  /**
   * 将选中元素滚动到容器可视区域
   * @param container 滚动容器
   * @param selected 选中的元素
   */
  private _scrollIntoView(container: HTMLElement, selected: HTMLElement) {
    if (!selected) {
      container.scrollTop = 0
      return
    }
    const offsetParents: HTMLElement[] = []
    let pointer = <HTMLElement>selected.offsetParent
    while (pointer && container !== pointer && container.contains(pointer)) {
      offsetParents.push(pointer)
      pointer = <HTMLElement>pointer.offsetParent
    }
    const top =
      selected.offsetTop +
      offsetParents.reduce((prev, curr) => prev + curr.offsetTop, 0)
    const bottom = top + selected.offsetHeight
    const viewRectTop = container.scrollTop
    const viewRectBottom = viewRectTop + container.clientHeight
    if (top < viewRectTop) {
      container.scrollTop = top
    } else if (bottom > viewRectBottom) {
      container.scrollTop = bottom - container.clientHeight
    }
  }

  /** 切换到上一个月 */
  private _preMonth() {
    this.now.setMonth(this.now.getMonth() - 1)
    this._update()
  }

  /** 切换到下一个月 */
  private _nextMonth() {
    this.now.setMonth(this.now.getMonth() + 1)
    this._update()
  }

  /** 切换到上一年 */
  private _preYear() {
    this.now.setFullYear(this.now.getFullYear() - 1)
    this._update()
  }

  /** 切换到下一年 */
  private _nextYear() {
    this.now.setFullYear(this.now.getFullYear() + 1)
    this._update()
  }

  /** 选中当前时刻并关闭面板 */
  private _now() {
    this.pickDate = new Date()
    this.dispose()
  }

  /**
   * 切换面板可见性
   * @param isVisible 是否可见
   */
  private _toggleVisible(isVisible: boolean) {
    if (isVisible) {
      this.dom.container.classList.add('active')
    } else {
      this.dom.container.classList.remove('active')
    }
  }

  /** 触发 onSubmit 回调，将选定日期格式化后传出 */
  private _submit() {
    if (this.options.onSubmit && this.pickDate) {
      const format = this.renderOptions?.dateFormat
      const pickDateString = this.formatDate(this.pickDate, format)
      this.options.onSubmit(pickDateString)
    }
  }

  /**
   * 按指定模板格式化日期
   * @param date 待格式化日期
   * @param format 格式模板，默认 yyyy-MM-dd hh:mm:ss
   * @returns 格式化后的日期字符串
   */
  public formatDate(date: Date, format = 'yyyy-MM-dd hh:mm:ss'): string {
    let dateString = format
    const dateOption = {
      'y+': date.getFullYear().toString(),
      'M+': (date.getMonth() + 1).toString(),
      'd+': date.getDate().toString(),
      'h+': date.getHours().toString(),
      'm+': date.getMinutes().toString(),
      's+': date.getSeconds().toString()
    }
    for (const k in dateOption) {
      const reg = new RegExp('(' + k + ')').exec(format)
      const key = <keyof typeof dateOption>k
      if (reg) {
        dateString = dateString.replace(
          reg[1],
          reg[1].length === 1
            ? dateOption[key]
            : dateOption[key].padStart(reg[1].length, '0')
        )
      }
    }
    return dateString
  }

  /**
   * 渲染日期选择器
   * @param option 渲染选项
   */
  public render(option: IRenderOption) {
    // 按需挂载到容器，避免初始化时就出现在页面上
    if (!this.dom.container.parentElement) {
      this.draw.getContainer().append(this.dom.container)
    }
    this.renderOptions = option
    this.lang = this._getLang()
    this._setLangChange()
    this._setValue()
    this._update()
    this._setPosition()
    this.isDatePicker = true
    this._toggleDateTimePicker()
    this._toggleVisible(true)
  }

  /** 隐藏并从 DOM 中移除面板 */
  public dispose() {
    this._toggleVisible(false)
    this.dom.container.remove()
  }

  /** 销毁选择器，移除容器 */
  public destroy() {
    this.dom.container.remove()
  }
}
